import { Layout, Txt } from "@revideo/2d";
import { DependencyContext, type SignalValue } from "@revideo/core";
import { accentRuns } from "@hudhud/core";
import { fontStack, fontWeight } from "@hudhud/reel/math";
import { balancedLines, breakLines, wordsOf, type TextRun } from "./wrap";

export type { TextRun } from "./wrap";

/** The canvas font for a family: the family first, its fallback stack behind it. */
export const family = (name: string) => fontStack(name);
/** The weight to ask for: a single-weight font at its own weight, so the canvas never fakes a bolder one. */
export const weight = (name: string, w: number) => fontWeight(name, w);

/** The text shadow a caption carries (the second, tight shadow of the CSS is folded into the stroke). */
export const CAPTION_SHADOW = { shadowColor: "rgba(0,0,0,0.75)", shadowBlur: 18, shadowOffset: [0, 4] as [number, number] };

const ARABIC = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
/** Whether a string carries right-to-left script (Arabic, Persian, Urdu). */
export const isRtlText = (text: string): boolean => ARABIC.test(text);

export interface ParagraphInput {
  runs: TextRun[];
  font: string; weight: number; size: number;
  /** px the lines may use */
  maxWidth: number;
  fill: string; accentFill?: string;
  align?: "left" | "center" | "right";
  /** CSS line-height as a multiplier */
  lineHeight: number;
  letterSpacing?: number;
  rtl: boolean;
  uppercase?: boolean;
  /** CSS `white-space: pre-line`: a newline in the text starts a new line (otherwise it is a space) */
  preLine?: boolean;
  /** CSS `text-wrap: balance`: the lines are broken at the narrowest width that keeps their number, so none is left short */
  balance?: boolean;
  /** node props applied to every line (shadow, stroke, opacity…) */
  line?: Partial<Record<string, SignalValue<unknown>>>;
  /** props on the column that holds the lines (margins, width…) */
  block?: Partial<Record<string, SignalValue<unknown>>>;
  /** props for the n-th word (the opener's "words" animation pops each in on its own) */
  word?: (index: number) => Partial<Record<string, SignalValue<unknown>>>;
}

let measurer: CanvasRenderingContext2D | null = null;
/** Width of `text` in the given font as the browser lays it out: the glyph advances, letter-spacing after every character. */
export function measureWidth(font: string, w: number, size: number, text: string, letterSpacing = 0): number {
  if (typeof document === "undefined") return text.length * (size * 0.55 + letterSpacing);
  measurer ??= document.createElement("canvas").getContext("2d");
  if (!measurer) return text.length * (size * 0.55 + letterSpacing);
  measurer.font = `${w} ${size}px ${family(font)}`;
  return measurer.measureText(text).width + letterSpacing * text.length;
}

/**
 * One line as a row of word nodes a space's width apart. The row is a Layout, not a Txt: a Txt lays its children out as one
 * run of text and ignores `gap`, so the words would touch. A right-to-left line runs the row backwards, so the first word ends up
 * on the right where a reader starts; each Arabic word is shaped by the canvas whichever way the row runs. Font properties are
 * inherited by the words from the row; the shape properties (fill, stroke, shadow) belong to each word.
 */
function lineNode(runs: TextRun[], p: ParagraphInput, firstIndex: number, gap: number): Layout {
  const text = (s: string) => (p.uppercase && !p.rtl ? s.toUpperCase() : s);
  const word = (r: TextRun, i: number) => new Txt({
    text: text(r.text), fill: r.accent ? p.accentFill ?? p.fill : p.fill,
    // Motion Canvas sizes a right-to-left word's offscreen cache for left-to-right text: give it room
    cachePadding: isRtlText(r.text) ? [p.size, p.size * 4] : 0,
    ...(p.line as object), ...(p.word?.(firstIndex + i) as object),
  });
  return new Layout({
    layout: true, direction: p.rtl ? "row-reverse" : "row", gap, alignItems: "baseline",
    fontFamily: family(p.font), fontWeight: weight(p.font, p.weight), fontSize: p.size,
    lineHeight: `${Math.round(p.lineHeight * 100)}%`, letterSpacing: p.letterSpacing ?? 0, textDirection: p.rtl ? "rtl" : "ltr",
    textAlign: p.align ?? "center",
    children: runs.length ? runs.map(word) : [new Txt({ text: " " })], // an empty line (two newlines in a row) still takes its height
  });
}

/**
 * A paragraph wrapped inside `maxWidth`, one row per line. The lines are broken here, with the page's fonts measured on a
 * scratch canvas, rather than by Motion Canvas' own wrapping: that draws every wrapped line of right-to-left text from the left
 * edge of the line's first logical word (and sizes its cache for left-to-right text), and cannot balance lines as the CSS reel does.
 *
 * The scene is built before a web font has necessarily arrived. When the face is not in yet, its loading is handed to the
 * renderer's dependency tracker (so no frame is drawn without it) and this pass measures the fallback face; the renderer runs
 * the scene again to draw (its media pass comes first), and that pass measures the real face. Building the lines inside the
 * font promise instead was tried and took the render page down at load.
 */
export function paragraph(p: ParagraphInput): Layout {
  const spec = `${weight(p.font, p.weight)} ${p.size}px ${family(p.font)}`;
  if (typeof document !== "undefined" && !document.fonts.check(spec)) DependencyContext.collectPromise(document.fonts.load(spec).catch(() => []));
  const measure = (s: string) => measureWidth(p.font, weight(p.font, p.weight), p.size, p.uppercase && !p.rtl ? s.toUpperCase() : s, p.letterSpacing ?? 0);
  const words = wordsOf(p.runs, !!p.preLine);
  const gap = measure(" ");
  let first = 0;
  const lines = (p.balance ? balancedLines : breakLines)(words, p.maxWidth, measure).map((line) => { const row = lineNode(line, p, first, gap); first += line.length; return row; });
  return new Layout({
    layout: true, direction: "column", alignItems: p.align === "left" ? "start" : p.align === "right" ? "end" : "center", width: p.maxWidth,
    children: lines, ...(p.block as object),
  });
}

/** A title's runs, ready for `paragraph`. */
export const runsOf = (text: string): TextRun[] => accentRuns(text).map((r) => ({ text: r.text, accent: r.accent }));
